import { z } from 'zod';

/**
 * Environment contract.
 *
 * Parsed once, at module load, so a missing or malformed variable fails loudly
 * at boot instead of as `undefined` inside a fetch call three screens later.
 * Everything here ships to the browser — never put a secret in a VITE_ var.
 */
/**
 * `URL.parse` without the baseline cost.
 *
 * The static parser only reached Safari 18, and this schema is evaluated at
 * module load, so calling it would fail to boot the whole app on an older
 * phone rather than merely disabling the socket. `new URL` throws on a value
 * the union's other branch will accept, so the throw is caught rather than
 * allowed to escape the refinement.
 */
const pathnameOf = (value: string): string | undefined => {
  try {
    return new URL(value).pathname;
  } catch {
    return undefined;
  }
};

const envSchema = z.object({
  VITE_API_BASE_URL: z
    .url({ message: 'VITE_API_BASE_URL must be an absolute URL' })
    .refine((value) => !value.endsWith('/'), 'VITE_API_BASE_URL must not end with "/"'),
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  VITE_ENABLE_API_MOCKS: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  /** Serve the high-volume mock fixtures instead of the small happy-path ones
   *  (six companies, long lists, multi-page tables). Only has an effect while
   *  VITE_ENABLE_API_MOCKS is on — it selects which data the mock layer
   *  answers with, and changes nothing about how the app behaves. */
  VITE_STRESS_MOCKS: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  /** The realtime server, e.g. `wss://…` or `https://…`. Optional: without it
   *  the live-refresh socket simply never connects and the portal falls back to
   *  refreshing on mutation, which is how it behaved before the socket existed.
   *
   *  Origin only — no path. socket.io reads a trailing path as a NAMESPACE, so
   *  `wss://host/ws` asks for a namespace the server does not define and the
   *  connection is refused with "Invalid namespace". The legacy portal carried
   *  that `/ws` suffix in its env for exactly this reason: it never connected
   *  there either, it just failed silently. Rejected at boot rather than
   *  debugged again later. */
  VITE_WEBSOCKET_SERVER_URL: z
    .union([
      z
        .url()
        .refine(
          (value) => pathnameOf(value) === '/',
          'VITE_WEBSOCKET_SERVER_URL must be an origin with no path — socket.io reads a path as a namespace',
        ),
      z.literal(''),
    ])
    .default(''),
  /** Stripe's PUBLISHABLE key — the `pk_` one, which is designed to ship in a
   *  client bundle. A `sk_` secret key here would be a live credential leak, so
   *  the prefix is enforced rather than assumed. Optional: the portal runs
   *  without it, and only the add-card form is unavailable. */
  VITE_APP_STRIPE_PUBLISHABLE_KEY: z
    .string()
    .refine(
      (value) => value === '' || value.startsWith('pk_'),
      'VITE_APP_STRIPE_PUBLISHABLE_KEY must be a publishable (pk_) key, never a secret key',
    )
    .default(''),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  const issues = parsed.error.issues
    .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
    .join('\n');
  throw new Error(`Invalid environment configuration:\n${issues}\n\nSee .env.example.`);
}

export const env = parsed.data;

export const isProduction = env.VITE_APP_ENV === 'production';
export const isDevelopment = env.VITE_APP_ENV === 'development';
